/**
 * PermissionsContext - Provides user permissions for meal plan editing
 *
 * This context provides permission flags that rarely change during a session,
 * preventing unnecessary re-renders when other data changes.
 */

import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

// Create the context
const PermissionsContext = createContext({
    // Permission flags
    canWrite: false,
    canPublish: false,
    canCreate: false,
    canRecommend: false,
    canViewNutrition: false,
    canManageParticipants: false,

    // User role info
    isOwner: false,
    isPro: false,
    isPatient: false,
    user: {},

    // Feature flags
    inhibitSwap: false,
    rdOverride: false,
});

// Custom hook for consuming permissions
export const usePermissions = () => {
    const context = useContext(PermissionsContext);
    if (!context) {
        throw new Error('usePermissions must be used within PermissionsProvider');
    }
    return context;
};

// Provider component
export const PermissionsProvider = ({
    children,
    permissions = {},
    user = {},
    profile = {},
    plan = {},
    isPro = false
}) => {
    // Memoize permissions to prevent unnecessary re-renders
    const value = useMemo(() => {
        const { rd_override = false } = profile?.preferences || {};
        const isOwner = plan?.owner === user?.uuid;
        const isPatient = profile?.type === 'patient';
        const inhibitSwap = user?.inhibit_swap && !rd_override;

        return {
            // Core permissions
            canWrite: permissions.canWrite || false,
            canPublish: permissions.canPublish || false,
            canCreate: permissions.canCreate || false,
            canRecommend: permissions.canRecommend !== false,
            canViewNutrition: !user?.hide_nutrition || rd_override,
            canManageParticipants: !isPatient && !inhibitSwap,

            // User role info
            isOwner,
            isPro,
            isPatient,
            user, // Expose user object for admin checks

            // Feature flags
            inhibitSwap,
            rdOverride: rd_override,
        };
    }, [permissions, user, profile, plan, isPro]);

    return (
        <PermissionsContext.Provider value={value}>
            {children}
        </PermissionsContext.Provider>
    );
};

PermissionsProvider.propTypes = {
    children: PropTypes.node.isRequired,
    permissions: PropTypes.shape({
        canWrite: PropTypes.bool,
        canPublish: PropTypes.bool,
        canCreate: PropTypes.bool,
        canRecommend: PropTypes.bool,
    }),
    user: PropTypes.object,
    profile: PropTypes.object,
    plan: PropTypes.object,
    isPro: PropTypes.bool,
};

export default PermissionsContext;
